import React, { useEffect, useState } from "react";
import { Col, Drawer, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import CustomList from "../../../../components/List";
import ListItem from "../../../../components/ListItem";
import CustomPagination from "../../../../components/CustomPagination";
import { useFinancialStatuses } from "../../../../../hooks/useDataHooks";
import { useReportDateRange } from "../../../../../hooks/useReportDateRange";
import API from "../../../../../API";
import useProcuringEntityPackage from "../../../../../hooks/useProcuringEntityPackage";
import ActionBar from "../../../../components/ActionBar";

const formatMonetaryValue = (jsonString) => {
  if (!jsonString) return "-";      
  try {
    const data =
      typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;
    if (!data || !data.amount) return "-";

    const formattedAmount = Number(data.amount).toLocaleString();
    const currency = data.currency || "";

    return `${currency} ${formattedAmount}`;
  } catch (error) {
    console.error("Error parsing monetary value:", error);
    return jsonString || "-";
  }
};

const certifiedAmount = { xxl: 4, xl: 4, lg: 4, md: 6, sm: 12, xs: 12 };
const paidAmount = { xxl: 5, xl: 5, lg: 5, md: 6, sm: 0, xs: 0 };
const ipc_no_description = { xxl: 7, xl: 7, lg: 7, md: 7, sm: 12, xs: 12 };
const ipcNo = { xxl: 2, xl: 2, lg: 2, md: 2, sm: 12, xs: 12 };
const balance = { xxl: 4, xl: 4, lg: 4, md: 3, sm: 0, xs: 0 };

const headerLayout = [
  { ...ipcNo, header: "IPC NO" },
  { ...ipc_no_description, header: "Description" },
  { ...certifiedAmount, header: "Certified Amount" },
  { ...paidAmount, header: "Paid Amount" },
  { ...balance, header: "Balance" },
];

const FinancialStatus = ({ match }) => {
  const [activeItem, setActiveItem] = useState(null);
  const [visible, setVisible] = useState(false);
  const {
    params: { packageId },
  } = match;
  const { procuringEntityPackage } = useProcuringEntityPackage(packageId);
  const contractId = procuringEntityPackage?.contract?.id;
  const { startDate, endDate } = useReportDateRange();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { financialStatuses = [], pagination, loading } = useFinancialStatuses(
    contractId,
    startDate,
    endDate,
    currentPage,
    pageSize
  );

  const handleRowClick = (item) => {
    setActiveItem(item);
    setVisible(true);
  };

  const handleCloseDrawer = () => {
    setVisible(false);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const formattedStartDate = startDate ? new Date(startDate).toISOString().split('T')[0] : null;
      const formattedEndDate = endDate ? new Date(endDate).toISOString().split('T')[0] : null;
      
      await API.exportFinancialStatus(formattedStartDate, formattedEndDate);
      message.success('Financial status report downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download financial status report. Please try again.');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const packageName = procuringEntityPackage?.name || "";

  return (
    <>
      <div style={{ padding: "16px" }}>
       <ActionBar actionButtonProp={{
        title: `${packageName || "==//=="}: Financial Status`,
        buttons: (
          <Button
            className="prst-button-secondary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadReport}
          >
            Download Report
          </Button>
        ),
       }} />
        <div className="prst-table">
        <CustomList
          itemName="Financial Status"
          showToolbar={false}
          items={financialStatuses || []}
          page={currentPage}
          itemCount={pagination?.total || (financialStatuses?.length ?? 0)}
          loading={loading}
          headerLayout={headerLayout}
          renderListItem={({ item }) => (
            <ListItem
              key={item.id}
              name={item?.status || "==//=="}
              item={item}
              isPackage={false}
              renderActions={() => false}
              onClick={() => handleRowClick(item)}
              style={{
                backgroundColor:
                  activeItem?.status === item.status
                    ? "#f0f0f0"
                    : "transparent",
              }}
            >
              <Col 
                {...ipcNo} 
                className="contentEllipse"
                data-label="IPC NO"
              >
                {item?.ipcNo || "==//=="}
              </Col>
              
              <Col 
                {...ipc_no_description} 
                className="contentEllipse"
                data-label="Description"
              >
                {item?.ipc_no_description?.length > 40 ? item?.ipc_no_description.slice(0, 40) + "..." : item?.ipc_no_description || "==//=="}
              </Col>
              <Col 
                {...certifiedAmount} 
                className="contentEllipse"
                data-label="Certified Amount"
              >
                {formatMonetaryValue(item?.certifiedAmount)}
              </Col>
              
              <Col 
                {...paidAmount} 
                className="contentEllipse"
                data-label="Paid Amount"
              >
                {formatMonetaryValue(item?.paidAmount)}
              </Col>

              <Col 
                {...balance} 
                className="contentEllipse"
                data-label="Balance"
              >
                {formatMonetaryValue(item?.remaining_package_balance)}
              </Col>
              
              <button 
                className="view-card-details" 
                onClick={() => handleRowClick(item)}
              >
                View Details
              </button>
            </ListItem>
          )}
        />
        </div>

        {pagination && (
          <CustomPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}

        {activeItem && (
          <Drawer
            title={`Details for IPC NO ${activeItem.ipcNo}`}
            placement="right"
            closable
            onClose={handleCloseDrawer}
            visible={visible}
            width={720}
          >
            <p>
              <strong>IPC NO:</strong> {activeItem.ipcNo || "==//=="}
            </p>
            <p>
              <strong>Description:</strong>{" "}
              {activeItem.ipc_no_description || "==//=="}
            </p>
            <p>
              <strong>Certified Amount:</strong>{" "}
              {formatMonetaryValue(activeItem.certifiedAmount)}
            </p>
            <p>
              <strong>Submission Date:</strong>{" "}
              {activeItem.submissionDate || "==//=="}
            </p>
            <p>
              <strong>Payment Due Date:</strong>{" "}
              {activeItem.paymentDueDate || "==//=="}
            </p>
            <p>
              <strong>Paid Date:</strong> {activeItem.paid_date || "==//=="}
            </p>
            <p>
              <strong>Paid Amount:</strong>{" "}
              {formatMonetaryValue(activeItem.paidAmount)}
            </p>
            <p>
              <strong>Unpaid (TSH):</strong>{" "}
                    {activeItem.ipc_no_description || "==//=="}
            </p>
            <p>
              <strong>Delayed Days:</strong> {activeItem.delayed_days || "==//=="}
            </p>
            <p>
              <strong>Payment Status:</strong> {activeItem.status || "==//=="}
            </p>
            <p>
              <strong>Remarks:</strong> {activeItem.remarks || "==//=="}
            </p>
            <p>
              <strong>Report Start Date:</strong>{" "}
              {activeItem.reportStartDate || "==//=="}
            </p>
            <p>
              <strong>Report End Date:</strong>{" "}
              {activeItem.reportEndDate || "==//=="}
            </p>
            <p>
              <strong>Balance:</strong>{" "}
              {formatMonetaryValue(activeItem.remaining_package_balance)}
            </p>
          </Drawer>
        )}
      </div>
    </>
  );
};

export default FinancialStatus;
